import {useCallback, useEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {useUser} from "../../providers/user-context";
import axios from "../../config/axios-config";
import {eventBus} from "../../event-bus";

const validView = data => Boolean(data?.supplierOrderId && data.versions && Array.isArray(data.lines) && Array.isArray(data.shipments));

export default function useOrderShipments({lineId, active, revision = 0}) {
    const user = useUser(), {i18n} = useTranslation();
    const scope = [lineId, active, user.tenantId, user.userIdentity, user.userRole, user.tenantType, i18n.language].join(":");
    const identity = useRef(scope); identity.current = scope;
    const read = useRef(), write = useRef(), attempt = useRef();
    const [state, setState] = useState({});
    const historyPage = useRef(0);
    const current = state.scope === scope ? state : {};
    const permitted = Boolean(user.tenantId && user.userIdentity && ["ADMIN", "OPERATOR", "SYSTEM"].includes(user.userRole)
        && ["IMPORTER", "PHARMACY"].includes(user.tenantType));
    const config = controller => ({signal: controller.signal, timeout: 15000, headers: {"Language-Context": i18n.language.toUpperCase()}});
    const load = useCallback(async (page = historyPage.current) => {
        historyPage.current = page;
        if (!active || !lineId || !permitted || write.current || attempt.current) {return;}
        read.current?.abort(); const controller = new AbortController(); read.current = controller;
        setState(previous => ({...(previous.scope === scope ? previous : {}), scope, loading: true, error: ""}));
        try {
            const {data} = await axios.get(`/api/v1/supplier-orders/by-line/${encodeURIComponent(lineId)}/shipments?page=${page}&size=10`, config(controller));
            if (!validView(data)) {throw new Error("Invalid shipments view");}
            if (identity.current === scope && !controller.signal.aborted) {setState({scope, data});}
        } catch (error) {
            if (identity.current === scope && !controller.signal.aborted) {
                setState(previous => ({...previous, scope, loading: false, conflict: true,
                    error: error.response?.status === 404 ? "SplitLegacyUnavailable" : "SplitLoadFailed"}));
            }
        }
    }, [scope, active, lineId, permitted, i18n.language]);

    useEffect(() => {
        attempt.current = null; historyPage.current = 0; setState({scope}); load(0);
        return () => {read.current?.abort(); write.current?.abort(); write.current = null;};
    }, [scope, load]);
    const priorRevision = useRef({scope, revision});
    useEffect(() => {
        if (priorRevision.current.scope === scope && priorRevision.current.revision !== revision) {load();}
        priorRevision.current = {scope, revision};
    }, [revision, scope, load]);

    const submit = async (operation, values) => {
        if (write.current || !current.data || current.conflict || !permitted) {return false;}
        if (!attempt.current) {
            if (operation === "create" ? user.tenantType !== "IMPORTER" || !current.data.canCreateShipment : !current.data.canCancelRemainder) {return false;}
            const body = {requestId: crypto.randomUUID(), expectedVersions: current.data.versions, quantities: values.quantities,
                ...(operation === "create" ? {deliveryCommitment: values.deliveryCommitment} : {reason: values.reason})};
            attempt.current = {operation, body, group: current.data.supplierOrderId};
        }
        const saved = attempt.current, controller = new AbortController(); write.current = controller;
        read.current?.abort(); setState(previous => ({...previous, pending: true, error: ""}));
        try {
            const path = saved.operation === "create" ? "shipments" : "remainder/cancel";
            const {data} = await axios.post(`/api/v1/supplier-orders/${saved.group}/${path}`, saved.body, config(controller));
            if (!validView(data) || data.supplierOrderId !== saved.group) {throw new Error("Invalid shipment receipt");}
            if (identity.current !== scope || controller.signal.aborted) {return false;}
            attempt.current = null; historyPage.current = 0; setState({scope, data}); eventBus.emit("refreshOrderPage"); return true;
        } catch (error) {
            if (identity.current !== scope || controller.signal.aborted) {return false;}
            const status = error.response?.status, uncertain = !status || status >= 500;
            if (!uncertain) {attempt.current = null;}
            setState(previous => ({...previous, pending: false, uncertain, conflict: status === 409,
                error: uncertain ? "SplitUncertain" : error.response?.data?.message || (status === 409 ? "SplitConflict" : "SplitWriteFailed")}));
            return false;
        } finally {if (write.current === controller) {write.current = null;}}
    };

    const assign = async (shipmentId, courierProfileId) => {
        if (write.current || attempt.current || !current.data || !permitted || current.conflict || current.loading || user.tenantType !== "IMPORTER") {return false;}
        const controller = new AbortController(); write.current = controller; read.current?.abort();
        setState(previous => ({...previous, pending: true}));
        try {
            const {data} = await axios.post(`/api/v1/supplier-orders/${current.data.supplierOrderId}/shipments/${shipmentId}/courier-assignment`,
                {courierProfileId}, config(controller));
            if (data.fulfillmentId !== shipmentId) {throw new Error("Invalid shipment assignment");}
            if (identity.current !== scope || controller.signal.aborted) {return false;}
            setState(previous => ({...previous, pending: false})); eventBus.emit("refreshOrderPage"); return true;
        } catch (error) {
            if (identity.current === scope && !controller.signal.aborted) {
                setState(previous => ({...previous, pending: false, conflict: true, error: "SplitAssignmentRefresh"}));
                throw error;
            }
            return false;
        } finally {if (write.current === controller) {write.current = null;}}
    };
    return {...current, permitted, refresh: () => load(), changePage: load, submit, assign, retry: () => submit(), retryOperation: attempt.current?.operation};
}
